import React from 'react';
import { View, ActivityIndicator } from 'react-native';
import { NavigationContainer } from '@react-navigation/native';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { getActiveRoutePath } from './src/utils/navigation';
import { addRecentRoute } from './src/utils/recent';
import { markCalcUsed } from './src/utils/usedCalcs';
import { CALC_ROUTE_SET } from './src/utils/calcRegistry';
import { Provider as PaperProvider, DefaultTheme, MD2DarkTheme as PaperDarkTheme } from 'react-native-paper';
import SettingsScreen, { DarkModeProvider, useDarkMode } from './src/screens/SettingsScreen';
import { UnitsProvider } from './src/context/UnitsContext';
import { StatusBar } from 'expo-status-bar';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useFonts } from 'expo-font';
import {
  Inter_400Regular,
  Inter_500Medium,
  Inter_700Bold,
} from '@expo-google-fonts/inter';
import {
  JetBrainsMono_400Regular,
  JetBrainsMono_700Bold,
} from '@expo-google-fonts/jetbrains-mono';
import { C } from './src/theme/tokens';

import HomeNavigator from './src/navigation/HomeNavigator';
import TABNavigator from './src/navigation/TABNavigator';
import CleanroomNavigator from './src/navigation/CleanroomNavigator';
import FumehoodNavigator from './src/navigation/FumehoodNavigator';
import BETNavigator from './src/navigation/BETNavigator';

const Tab = createBottomTabNavigator();

// Field Instrument Dark Theme
const appleGlassTheme = {
  ...PaperDarkTheme,
  dark: true,
  colors: {
    ...PaperDarkTheme.colors,
    primary: C.accent,
    background: C.bg,
    surface: C.panel,
    surfaceVariant: C.panel2,
    text: C.ink,
    onSurface: C.ink,
    onSurfaceVariant: C.ink2,
    placeholder: C.dim,
    outline: C.edge,
    outlineVariant: C.edge,
    error: C.danger,
    errorContainer: 'rgba(255,69,58,0.12)',
    onPrimary: '#FFFFFF',
    primaryContainer: 'rgba(255,107,26,0.12)',
    onPrimaryContainer: C.ink,
    surfaceDisabled: C.panel2,
    onSurfaceDisabled: C.dim,
    inverseSurface: C.ink,
    inverseOnSurface: C.bg,
    backdrop: 'rgba(0,0,0,0.6)',
    elevation: {
      level0: 'transparent',
      level1: C.panel,
      level2: C.panel2,
      level3: C.panel2,
      level4: C.panel2,
      level5: C.panel2,
    },
  },
};

// Legacy light theme (kept for compatibility)
const legacyTheme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    primary: '#1976D2',
    accent: '#FF6B35',
    background: '#F5F5F5',
    surface: '#FFFFFF',
    text: '#212121',
    error: '#D32F2F',
  },
};

// Theme wrapper component - Always use Apple Glass Dark Theme
function ThemedApp() {
  const { isDarkMode } = useDarkMode();
  // Force Apple Glass theme (dark mode always)
  const theme = appleGlassTheme;

  // Navigation theme with glassmorphism
  const navigationTheme = {
    dark: true,
    colors: {
      primary: theme.colors.primary,
      background: theme.colors.background,
      card: 'rgba(28, 28, 30, 0.72)',  // Glass card
      text: theme.colors.text,
      border: 'rgba(255, 255, 255, 0.15)',
      notification: theme.colors.accent,
    },
  };

  return (
    <PaperProvider theme={theme}>
      <NavigationContainer
        theme={navigationTheme}
        onStateChange={(state) => {
          const path = getActiveRoutePath(state);
          const last = path[path.length - 1];
          if (!last || last === 'HomeMain' || last === 'RecentCalculations' || last === 'Progress' || last === 'History') return;
          void addRecentRoute({ path, title: last });
          if (CALC_ROUTE_SET.has(last)) void markCalcUsed(last);
        }}
      >
        <StatusBar style={isDarkMode ? 'light' : 'dark'} />
        <Tab.Navigator
          screenOptions={({ route }) => ({
            headerShown: false,
            tabBarIcon: ({ focused, color, size }) => {
              let iconName: keyof typeof Ionicons.glyphMap;
              if (route.name === 'Home') {
                iconName = focused ? 'home' : 'home-outline';
              } else if (route.name === 'TAB') {
                iconName = focused ? 'clipboard' : 'clipboard-outline';
              } else if (route.name === 'Cleanroom') {
                iconName = focused ? 'shield-checkmark' : 'shield-checkmark-outline';
              } else if (route.name === 'Fumehood') {
                iconName = focused ? 'flask' : 'flask-outline';
              } else if (route.name === 'BET') {
                iconName = 'business-outline';
              } else if (route.name === 'Settings') {
                iconName = focused ? 'settings' : 'settings-outline';
              } else {
                iconName = 'help-outline';
              }
              return <Ionicons name={iconName} size={size} color={color} />;
            },
            tabBarActiveTintColor: C.accent,
            tabBarInactiveTintColor: C.dim,
            tabBarStyle: {
              backgroundColor: C.panel,
              borderTopColor: C.edge,
              borderTopWidth: 1,
            },
            tabBarLabelStyle: {
              fontSize: 11,
            },
            headerStyle: {
              backgroundColor: theme.colors.primary,
            },
            headerTintColor: '#fff',
          })}
        >
          <Tab.Screen name="Home" component={HomeNavigator} />
          <Tab.Screen name="TAB" component={TABNavigator} />
          <Tab.Screen name="Cleanroom" component={CleanroomNavigator} />
          <Tab.Screen name="Fumehood" component={FumehoodNavigator} options={{ tabBarLabel: 'Fume Hood' }} />
          <Tab.Screen name="BET" component={BETNavigator} />
          <Tab.Screen name="Settings" component={SettingsScreen} />
        </Tab.Navigator>
      </NavigationContainer>
    </PaperProvider>
  );
}

export default function App() {
  const [fontsLoaded] = useFonts({
    Inter_400Regular,
    Inter_500Medium,
    Inter_700Bold,
    JetBrainsMono_400Regular,
    JetBrainsMono_700Bold,
  });

  if (!fontsLoaded) {
    return (
      <View style={{ flex: 1, backgroundColor: C.bg, alignItems: 'center', justifyContent: 'center' }}>
        <ActivityIndicator color={C.accent} />
      </View>
    );
  }

  return (
    <SafeAreaProvider>
      <DarkModeProvider>
        <UnitsProvider>
          <ThemedApp />
        </UnitsProvider>
      </DarkModeProvider>
    </SafeAreaProvider>
  );
}
